import {Struct} from '@wandb/weave/common/util/types';
import * as React from 'react';
import {useEffect, useMemo} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Card} from 'semantic-ui-react';

import * as Track from '../services/analytics';
import {labelComponent, makeCaptions} from '../util/media';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {getObject3DMediaPath, MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import {SingleRunLink} from './MultiRunWorkspace/SingleRunMode';
import PointCloud from './PointCloud';
import Viewer3D from './Viewer3D';

export const Object3DCard: React.FunctionComponent<MediaCardProps> = React.memo(
  props => {
    const {
      run,
      globalStep,
      mediaKey,
      mediaIndex,
      runSignature,
      height,
      width,
      mediaPanelRef,
      tileMedia,
      loading,
      customRunName,
      config,
      tileMediaSpec,
      getMediaTracking,
    } = props;

    const mediaPath =
      getObject3DMediaPath(
        {historyRow: tileMedia?.historyRow, mediaKey},
        mediaIndex
      ) ?? '';

    const isPointCloud = mediaPath.endsWith('.pts.json');
    // In the past, not all point cloud media files were uploaded with the
    // pts.json extension. This is a workaround to allow those files to still
    // be viewed. It can be removed in the future assuming the below analytics
    // does not fire often
    const sdkBugWorkaround = !isPointCloud && mediaPath.endsWith('.json');

    const Media3DComponent =
      isPointCloud || sdkBugWorkaround ? PointCloud : Viewer3D;
    const object3DBackgroundColor = config.object3DBackgroundColor;
    const pointColor =
      config.pointColor?.type === 'rgb' ? config.pointColor.color : undefined;

    useEffect(() => {
      if (sdkBugWorkaround) {
        Track.pointCloudFileWorkaroundUsed();
      }
    }, [sdkBugWorkaround]);
    const currentMediaMetadata: Struct | undefined =
      tileMedia?.historyRow?.[mediaKey];

    const titleLink = (
      <SingleRunLink
        {...runSignature}
        linkText={customRunName ?? run.displayName}
      />
    );

    const captions = useMemo(() => {
      return makeCaptions(currentMediaMetadata, mediaIndex);
    }, [currentMediaMetadata, mediaIndex]);

    return (
      <Card
        data-test={`object3D-${
          isPointCloud || sdkBugWorkaround ? 'PointCloud' : 'Viewer3D'
        }`}
        className="object3D-card"
        style={{width}}>
        {labelComponent(props, tileMedia?.step, titleLink)}

        {loading ? (
          <Loader name="panel-content-loader" size="small" />
        ) : tileMedia == null ? (
          <MessageMediaNotFound
            mediaKey={mediaKey}
            stepIndex={globalStep}
            mediaIndex={mediaIndex}
            mediaType="object3D"
          />
        ) : (
          <div
            style={{
              width,
              height,
            }}>
            <Media3DComponent
              width={width}
              height={height}
              url={tileMedia.directURL}
              blob={tileMedia.blob}
              controls={props.controls}
              mediaKey={mediaKey}
              mediaPanelRefId={mediaPanelRef.id}
              backgroundColor={object3DBackgroundColor}
              pointColor={pointColor}
              useRightHandedSystem={config.useRightHandedSystem}
              tileMedia={tileMedia}
              tileMediaSpec={tileMediaSpec}
              getMediaTracking={getMediaTracking}
            />
          </div>
        )}
        {/* CAPTIONS */}
        {captions.length > 0 && (
          <div className="image-card-caption">{captions}</div>
        )}
      </Card>
    );
  }
);

export default Object3DCard;
